/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { ApiError } from '@/scapi';

/**
 * Enum of every coupon-apply error reason that survives a page redirect.
 *
 * Mirrors the value-definitions on the `Basket.couponLastError` custom
 * attribute (see `cartridges/site_import/meta/system-objecttype-extensions.xml`).
 *
 * The matching i18n keys live under `cart:promoCode.error.<key>` —
 * `errorI18nKey()` below is the single place that maps the enum to a key
 * so the form, the action, and the cart loader all render identical copy.
 */
export const CouponErrorCode = {
    INVALID_COUPON_CODE: 'INVALID_COUPON_CODE',
    COUPON_CODE_ALREADY_IN_BASKET: 'COUPON_CODE_ALREADY_IN_BASKET',
    COUPON_CODE_NOT_APPLICABLE: 'COUPON_CODE_NOT_APPLICABLE',
    COUPON_CODE_EXPIRED: 'COUPON_CODE_EXPIRED',
    NO_ACTIVE_PROMOTION: 'NO_ACTIVE_PROMOTION',
    TOO_MANY_ATTEMPTS: 'TOO_MANY_ATTEMPTS',
    UNKNOWN: 'UNKNOWN',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional const + type companion pattern
export type CouponErrorCode = (typeof CouponErrorCode)[keyof typeof CouponErrorCode];

/**
 * Substring matches against the SCAPI `error.body.type` URI (RFC 7807).
 *
 * Order matters: more-specific patterns must come before more-general ones
 * (e.g. `coupon-code-already-in-basket` before a plain `coupon-already`).
 *
 * SCAPI emits these as URIs like
 * `https://api.commercecloud.salesforce.com/documentation/error/v1/errors/coupon-code-already-in-basket`.
 * We match on the slug portion so the helper is resilient to host or version
 * changes. Each entry also includes the legacy SFCC exception-class name
 * (`*Exception`) so OCAPI / WebDAV-shaped errors map the same way.
 *
 * Exported so the SCAPI integration test (which holds the raw
 * `ScapiError.body` text rather than an `ApiError` instance) can assert
 * the live-sandbox fault body matches the same pattern set the React
 * action uses to bucket the fault.
 */
export const FAULT_PATTERNS: ReadonlyArray<{ pattern: RegExp; code: CouponErrorCode }> = [
    {
        pattern:
            /coupon[-_\s]code[-_\s]already[-_\s]in[-_\s]basket|coupon[-_\s]already[-_\s]in[-_\s]basket|CouponAlreadyInBasketException/i,
        code: CouponErrorCode.COUPON_CODE_ALREADY_IN_BASKET,
    },
    {
        pattern: /coupon[-_\s]code[-_\s]expired|coupon[-_\s]expired|CouponCodeExpiredException/i,
        code: CouponErrorCode.COUPON_CODE_EXPIRED,
    },
    {
        pattern:
            /coupon[-_\s]code[-_\s]not[-_\s]applicable|coupon[-_\s]not[-_\s]applicable|CouponCodeNotApplicableException/i,
        code: CouponErrorCode.COUPON_CODE_NOT_APPLICABLE,
    },
    {
        pattern: /no[-_\s]active[-_\s]promotion|NoActivePromotionException/i,
        code: CouponErrorCode.NO_ACTIVE_PROMOTION,
    },
    {
        pattern:
            /coupon[-_\s]code[-_\s]unknown|coupon[-_\s]invalid|invalid[-_\s]coupon|CouponInvalidException|CouponCodeUnknownException/i,
        code: CouponErrorCode.INVALID_COUPON_CODE,
    },
];

/**
 * Maps a SCAPI fault (or any thrown error) to a CouponErrorCode enum.
 *
 * Detection precedence:
 *   1. `ApiError.body.type` URI slug (the canonical SCAPI discriminator).
 *   2. `ApiError.body.title` (some SFCC error envelopes carry the legacy
 *      exception class in the title rather than the type).
 *   3. `ApiError.body.detail` (last-resort substring match).
 *   4. Non-ApiError errors → `UNKNOWN` so the caller renders the generic
 *      message without leaking the underlying exception to the shopper.
 *
 * NEVER passes the submitted coupon code through — that input must be
 * scrubbed by the caller. This helper only sees error metadata.
 *
 * @param error - Anything thrown by the SCAPI client, usually an `ApiError`.
 * @returns A `CouponErrorCode` enum value. Defaults to `UNKNOWN`.
 */
export function couponFaultToEnum(error: unknown): CouponErrorCode {
    if (!(error instanceof ApiError)) {
        return CouponErrorCode.UNKNOWN;
    }

    const haystack = [error.body?.type, error.body?.title, error.body?.detail]
        .filter((field): field is string => typeof field === 'string' && field.length > 0)
        .join('\n');

    if (haystack.length === 0) {
        return CouponErrorCode.UNKNOWN;
    }

    for (const { pattern, code } of FAULT_PATTERNS) {
        if (pattern.test(haystack)) {
            return code;
        }
    }

    return CouponErrorCode.UNKNOWN;
}

/**
 * Returns the cart-namespaced i18n key for a given coupon error enum.
 *
 * Single source of truth — every consumer (form inline error, toast,
 * cart loader on redirect) reads through this so adding a new enum
 * is one edit here plus one translation entry.
 *
 * Two variants per interpolated message: the "live-submit" variant
 * (default, `hasCode: true`) expects a `{{code}}` interpolation param
 * carrying the submitted promo string; the "persisted" variant
 * (`hasCode: false`) is used by the redirect-survival path where the
 * original input isn't available and would render as an empty
 * `Promo code ""` if interpolated.
 *
 * @param code - The CouponErrorCode enum value.
 * @param opts.hasCode - Whether the caller can supply the submitted
 *     promo code for `{{code}}` interpolation. Defaults to true.
 * @returns A fully-qualified i18n key, e.g. `cart:promoCode.error.alreadyApplied`.
 */
export function errorI18nKey(code: CouponErrorCode, opts?: { hasCode?: boolean }): string {
    const hasCode = opts?.hasCode ?? true;
    switch (code) {
        case CouponErrorCode.COUPON_CODE_ALREADY_IN_BASKET:
            return hasCode ? 'cart:promoCode.error.alreadyApplied' : 'cart:promoCode.error.alreadyAppliedGeneric';
        case CouponErrorCode.COUPON_CODE_NOT_APPLICABLE:
            // "Promo code is valid, but may not apply to all items in the
            // cart." — no code interpolation needed, single key.
            return 'cart:promoCode.error.notApplicable';
        case CouponErrorCode.TOO_MANY_ATTEMPTS:
            // Rate-limit copy is intentionally distinct from the SFCC
            // status-code table (not a coupon fault, our own guardrail).
            return 'cart:promoCode.error.tooManyAttempts';
        case CouponErrorCode.INVALID_COUPON_CODE:
        case CouponErrorCode.COUPON_CODE_EXPIRED:
        case CouponErrorCode.NO_ACTIVE_PROMOTION:
        case CouponErrorCode.UNKNOWN:
        default:
            return hasCode ? 'cart:promoCode.errorMessage' : 'cart:promoCode.errorMessageGeneric';
    }
}

/**
 * Companion to `errorI18nKey` — returns the cart-namespaced i18n key for the
 * Alert's bold *title* row (design pilot, Figma node 37470:86929). Every
 * error variant has its own concise title; codes that render the shared
 * generic body all resolve to the same generic `errorTitle`.
 *
 * @param code - The CouponErrorCode enum value.
 * @returns A fully-qualified i18n key, e.g. `cart:promoCode.error.alreadyAppliedTitle`.
 */
export function titleI18nKey(code: CouponErrorCode): string {
    switch (code) {
        case CouponErrorCode.COUPON_CODE_ALREADY_IN_BASKET:
            return 'cart:promoCode.error.alreadyAppliedTitle';
        case CouponErrorCode.COUPON_CODE_NOT_APPLICABLE:
            return 'cart:promoCode.error.notApplicableTitle';
        case CouponErrorCode.TOO_MANY_ATTEMPTS:
            return 'cart:promoCode.error.tooManyAttemptsTitle';
        case CouponErrorCode.INVALID_COUPON_CODE:
        case CouponErrorCode.COUPON_CODE_EXPIRED:
        case CouponErrorCode.NO_ACTIVE_PROMOTION:
        case CouponErrorCode.UNKNOWN:
        default:
            return 'cart:promoCode.errorTitle';
    }
}
